<script setup>
import UnhideAllActionButton from "@/components/actions/partials/UnhideAllActionButton.vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import {computed} from "vue";
import {errorToast, successToast} from "@/composables/useToasts";

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
});

const queryClient = useQueryClient();

const {mutate: unhideAction} = useMutation({
  mutationFn: pageId => axios.post(`/ajax/v2/actions/page/${pageId}/unhide`),
  onMutate: async (pageId) => {
    await queryClient.cancelQueries({ queryKey: ['hidden-actions-by-page'] });
    await queryClient.cancelQueries({ queryKey: ['actions'] });

    const previousActions = queryClient.getQueryData(['hidden-actions-by-page']);

    queryClient.setQueryData(['hidden-actions-by-page'], (old) => old ? old.filter(page => page.id !== pageId) : []);

    successToast('Page actions unhidden');

    return { previousActions }
  },
  onError: (error, _, context) => {
    const {message} = error.response.data;

    errorToast(message ?? 'Something went wrong unhiding actions for page');

    queryClient.setQueryData(['hidden-actions-by-page'], context.previousActions);
  },
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['actions'] });
    queryClient.invalidateQueries({ queryKey: ['hidden-actions-by-page'] });
  },
});

const actionText = computed(() => props.page.action_count === 1 ? '1 Action' : `${props.page.action_count} Actions`);
</script>

<template>
  <div
    class="tw-border tw-rounded-ld tw-border-grey-mid tw-rounded-lg tw-bg-white"
  >
    <div class="tw-flex tw-gap-6 tw-p-6 tw-items-center tw-justify-between">
      <a
        class="tw-text-blue tw-text-base"
        :href="page.url"
        target="_blank"
        @click.self="() => {}"
      >
        {{ page.url }}
      </a>

      <div class="tw-flex tw-items-center tw-gap-6">
        <span class="tw-flex tw-items-center tw-gap-2 tw-text-grey">
          <svg-vue
            icon="icons/signal-bars"
            class="tw-w-5 tw-h-5 tw-flex-shrink-0"
          />
          <span class="tw-text-base">
            {{ actionText }}
          </span>
        </span>
        <UnhideAllActionButton @unhide="unhideAction(page.id)" />
      </div>
    </div>
  </div>
</template>
